import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import heroImage from '../../assets/images/ProgrammeIA-hero.webp';

const INDIGO = '#7C3AED';
const INDIGO_LIGHT = '#EDE9FE';
const YELLOW = '#FBB900';
const YELLOW_DARK = '#D89B00';
const TEAL = '#00919E';

const Container = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto w-full max-w-[1200px] px-6">{children}</div>
);

const Arrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SectionHeading = ({
  eyebrow,
  title,
  emphasis,
  intro,
  centered = true,
  light = false,
}: {
  eyebrow: string;
  title: string;
  emphasis?: string;
  intro?: string;
  centered?: boolean;
  light?: boolean;
}) => (
  <div
    style={{
      textAlign: centered ? 'center' : 'left',
      maxWidth: 820,
      margin: centered ? '0 auto 56px' : '0 0 48px',
    }}
  >
    <p
      style={{
        fontSize: 13,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.10em',
        color: light ? YELLOW : INDIGO,
        margin: '0 0 12px',
      }}
    >
      {eyebrow}
    </p>
    <h2
      style={{
        display: 'inline-block',
        position: 'relative',
        fontSize: 38,
        fontWeight: 700,
        lineHeight: 1.15,
        margin: 0,
        color: light ? '#fff' : '#111827',
        letterSpacing: '-0.01em',
        textWrap: 'balance' as React.CSSProperties['textWrap'],
      }}
    >
      {title}
      {emphasis && (
        <>
          {' '}
          <span style={{ color: light ? YELLOW : INDIGO }}>{emphasis}</span>
        </>
      )}
      <span
        style={{
          position: 'absolute',
          left: centered ? '50%' : 0,
          transform: centered ? 'translateX(-50%)' : 'none',
          bottom: -10,
          width: 60,
          height: 4,
          background: YELLOW,
          borderRadius: 2,
        }}
      />
    </h2>
    {intro && (
      <p
        style={{
          marginTop: 28,
          fontSize: 17,
          lineHeight: 1.7,
          color: light ? '#D1D5DB' : '#4B5563',
        }}
      >
        {intro}
      </p>
    )}
  </div>
);

/* ============================================================
   Hero
   ============================================================ */
const Hero = () => (
  <section
    style={{
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #FAF7FF 0%, #FFFFFF 55%, #FFFAF0 100%)',
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: -160,
        right: -120,
        width: 460,
        height: 460,
        background: `${INDIGO}1A`,
        borderRadius: '50%',
        filter: 'blur(70px)',
      }}
    />
    <div
      style={{
        position: 'absolute',
        top: '55%',
        left: -120,
        width: 320,
        height: 320,
        background: 'rgba(251,185,0,0.10)',
        borderRadius: '50%',
        filter: 'blur(50px)',
      }}
    />

    <Container>
      <div className="grid gap-12 md:gap-16 lg:gap-[72px] items-center md:grid-cols-[1.05fr_1fr]" style={{ position: 'relative', padding: '72px 0 130px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
          <div style={{ marginBottom: 22 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: INDIGO_LIGHT,
                color: INDIGO,
                padding: '8px 16px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '0.04em',
              }}
            >
              <span style={{ width: 8, height: 8, background: INDIGO, borderRadius: '50%' }} />
              Programme Innovation
            </span>
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-[56px]"
            style={{
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              color: '#111827',
              margin: '0 0 20px',
              textWrap: 'balance' as React.CSSProperties['textWrap'],
            }}
          >
            Préparer nos enfants au{' '}
            <span style={{ position: 'relative', display: 'inline-block', color: INDIGO }}>
              monde de demain
              <svg
                style={{ position: 'absolute', left: 0, right: 0, bottom: -10, width: '100%', height: 14, color: `${INDIGO}59` }}
                viewBox="0 0 200 12"
                preserveAspectRatio="none"
              >
                <path d="M0 8 Q50 0 100 8 T200 8" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round" />
              </svg>
            </span>
          </h1>
          <p style={{ fontSize: 20, lineHeight: 1.5, color: '#374151', margin: '0 0 16px', fontWeight: 500 }}>
            Une initiation à l'intelligence artificielle, sécurisée et encadrée, dès le primaire.
          </p>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: '#4B5563', maxWidth: 560, margin: '0 0 36px' }}>
            À l'école, les outils que les enfants rencontreront toute leur vie ne s'apprennent plus en cours de route. Au Groupe Scolaire Sanaa, nous accompagnons nos élèves dans la découverte de l'intelligence artificielle — non pas pour faire à leur place, mais pour leur apprendre à comprendre, questionner et utiliser ces technologies en confiance.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Link
              to="/inscription"
              style={{
                background: INDIGO,
                color: '#fff',
                padding: '14px 26px',
                borderRadius: 6,
                fontSize: 16,
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: `0 10px 24px -10px ${INDIGO}80`,
              }}
            >
              Inscrire mon enfant
              <Arrow />
            </Link>
            <Link
              to="/contact"
              style={{
                color: '#374151',
                fontSize: 16,
                fontWeight: 500,
                textDecoration: 'none',
                padding: '14px 8px',
              }}
            >
              Nous contacter →
            </Link>
          </div>

          <div
            style={{
              marginTop: 40,
              paddingTop: 24,
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              gap: 28,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#6B7280',
              }}
            >
              Outils utilisés
            </span>
            <div
              style={{
                display: 'flex',
                gap: 18,
                alignItems: 'center',
                color: '#374151',
                fontSize: 14,
                fontWeight: 500,
                flexWrap: 'wrap',
              }}
            >
              <span>Gemini</span>
              <span style={{ color: '#D1D5DB' }}>·</span>
              <span>Canva Éducation</span>
              <span style={{ color: '#D1D5DB' }}>·</span>
              <span>Google for Education</span>
              <span style={{ color: '#D1D5DB' }}>·</span>
              <span>Makeblock</span>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <HeroIllustration />
        </motion.div>
      </div>
    </Container>

    <svg
      viewBox="0 0 1440 120"
      preserveAspectRatio="none"
      style={{ display: 'block', width: '100%', height: 80, position: 'absolute', bottom: 0, left: 0 }}
    >
      <path
        d="M0 0L48 8.875C96 17.75 192 35.5 288 53.25C384 71 480 88.75 576 84.875C672 81 768 55.5 864 44.375C960 33.25 1056 36.5 1152 48.625C1248 60.75 1344 81.75 1392 92.25L1440 102.75V120H0V0Z"
        fill="#fff"
      />
    </svg>
  </section>
);

const HeroIllustration = () => (
  <div style={{ position: 'relative', maxWidth: 600, marginLeft: 'auto', width: '100%' }}>
    <div
      style={{
        position: 'absolute',
        inset: -14,
        background: `linear-gradient(135deg, ${INDIGO}26, ${YELLOW}26)`,
        borderRadius: 28,
        transform: 'rotate(2deg)',
      }}
    />
    <div
      style={{
        position: 'absolute',
        top: -22,
        left: -22,
        width: 110,
        height: 110,
        background: YELLOW,
        borderRadius: 18,
        zIndex: 0,
      }}
    />
    <div
      style={{
        position: 'relative',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 30px 60px -18px rgba(76,29,149,0.30)',
        outline: '1px solid rgba(0,0,0,0.04)',
        zIndex: 1,
      }}
    >
      <img
        src={heroImage}
        alt="Illustration : un robot bienveillant, un livre, une tablette et des crayons de couleur"
        style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '16/9', objectFit: 'cover' }}
      />
    </div>
    <div
      style={{
        position: 'absolute',
        bottom: -20,
        right: -20,
        width: 84,
        height: 84,
        background: TEAL,
        borderRadius: 16,
        zIndex: 0,
      }}
    />
    <div
      style={{
        position: 'absolute',
        bottom: -28,
        left: '8%',
        background: '#fff',
        borderRadius: 14,
        padding: '12px 18px',
        boxShadow: '0 16px 36px -12px rgba(0,0,0,0.18)',
        border: '1px solid #F3F4F6',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        zIndex: 2,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: `${INDIGO}1A`,
          display: 'grid',
          placeItems: 'center',
          color: INDIGO,
          flexShrink: 0,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path
            d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09zM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Dès le primaire</div>
        <div style={{ fontSize: 11, color: '#6B7280' }}>Encadré · sécurisé</div>
      </div>
    </div>
  </div>
);

/* ============================================================
   Pourquoi
   ============================================================ */
const Pourquoi = () => {
  const levers = [
    { k: 'Poser', d: 'des questions précises' },
    { k: 'Vérifier', d: 'ce que la machine répond' },
    { k: 'Garder', d: 'son discernement' },
  ];
  const leverTones = [
    { bg: 'rgba(251,185,0,0.12)', fg: YELLOW_DARK },
    { bg: 'rgba(0,145,158,0.12)', fg: TEAL },
    { bg: `${INDIGO}1F`, fg: INDIGO },
  ];
  return (
    <section style={{ padding: '96px 0 80px', background: '#fff' }}>
      <Container>
        <SectionHeading
          eyebrow="Pourquoi maintenant"
          title="Une compétence du quotidien,"
          emphasis="pas une matière du futur"
        />
        <div className="grid gap-12 md:grid-cols-[1.1fr_1fr] md:gap-[72px] items-center">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, color: '#374151', fontSize: 17, lineHeight: 1.75 }}>
            <p style={{ margin: 0 }}>
              L'intelligence artificielle n'est plus une promesse lointaine. Elle est déjà partout : dans les recherches en ligne, les recommandations, les outils créatifs, les assistants vocaux. Nos élèves grandissent dans un monde où savoir dialoguer avec une IA, comprendre ses limites et garder son esprit critique deviendra aussi naturel que de lire ou d'écrire.
            </p>
            <p style={{ margin: 0 }}>
              Plutôt que de subir cette évolution, nous avons choisi de l'<strong style={{ color: '#111827', fontWeight: 600 }}>accompagner</strong>. Au primaire, l'esprit est ouvert, la curiosité est forte, et c'est l'âge idéal pour poser les bonnes habitudes : poser de bonnes questions, vérifier les réponses, garder son discernement.
            </p>
            <p
              style={{
                margin: 0,
                paddingLeft: 20,
                borderLeft: `3px solid ${INDIGO}`,
                fontSize: 18,
                color: '#111827',
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              Le Programme IA du Groupe Scolaire Sanaa n'est pas un cours d'informatique. C'est une éducation à la pensée — apprendre à utiliser un outil puissant en gardant la main, le doute, et la curiosité.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {levers.map((it, i) => (
              <motion.div
                key={it.k}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 18,
                  background: '#fff',
                  border: '1px solid #F3F4F6',
                  padding: '18px 22px',
                  borderRadius: 14,
                  boxShadow: '0 10px 30px -18px rgba(0,0,0,0.10)',
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    flexShrink: 0,
                    background: leverTones[i].bg,
                    color: leverTones[i].fg,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{it.k}</div>
                  <div style={{ fontSize: 14, color: '#6B7280' }}>{it.d}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

/* ============================================================
   Approche
   ============================================================ */
type Tone = 'yellow' | 'indigo' | 'teal';
const Approche = () => {
  const pillars: { title: string; body: string; icon: React.ReactNode; tone: Tone }[] = [
    {
      title: 'Découvrir avec curiosité',
      body: "L'IA est introduite comme un outil parmi d'autres — au même titre qu'un livre, une carte ou une calculatrice. L'enfant apprend à la solliciter pour explorer, créer, comprendre.",
      tone: 'yellow',
      icon: (
        <path
          d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: 'Toujours en cadre supervisé',
      body: "Aucune utilisation libre. Les enseignants préparent les activités, accompagnent chaque échange, et corrigent les usages. L'école assure un environnement de comptes scolaires sécurisés (Google for Education).",
      tone: 'indigo',
      icon: (
        <path
          d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: 'Lien école-famille',
      body: 'Chaque temps fort en classe peut être prolongé à la maison par une discussion ou une activité simple. Les parents reçoivent les bons repères pour accompagner leur enfant à la maison.',
      tone: 'teal',
      icon: (
        <path
          d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
  ];
  const tones: Record<Tone, { bg: string; fg: string }> = {
    yellow: { bg: 'rgba(251,185,0,0.10)', fg: YELLOW_DARK },
    indigo: { bg: `${INDIGO}1A`, fg: INDIGO },
    teal: { bg: 'rgba(0,145,158,0.10)', fg: TEAL },
  };
  return (
    <section style={{ padding: '80px 0', background: '#F9FAFB', position: 'relative', overflow: 'hidden' }}>
      <Container>
        <SectionHeading
          eyebrow="Notre approche"
          title="Une pédagogie encadrée,"
          emphasis="jamais en autonomie totale"
          intro="Nos élèves ne sont jamais seuls face à l'écran. Chaque interaction avec une IA se fait sous la supervision de leur enseignant, dans le cadre d'une activité préparée, avec une intention pédagogique claire."
        />
        <div className="grid gap-6 md:grid-cols-3">
          {pillars.map((p, i) => {
            const t = tones[p.tone];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: 32,
                  boxShadow: '0 10px 30px -10px rgba(0,0,0,0.08)',
                  border: '1px solid #F3F4F6',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: -28,
                    right: -28,
                    width: 110,
                    height: 110,
                    background: t.bg,
                    borderRadius: '50%',
                    opacity: 0.7,
                  }}
                />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      background: t.bg,
                      color: t.fg,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                      {p.icon}
                    </svg>
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.10em',
                      textTransform: 'uppercase',
                      color: t.fg,
                    }}
                  >
                    Pilier {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    margin: 0,
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#111827',
                    lineHeight: 1.25,
                  }}
                >
                  {p.title}
                </h3>
                <p
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    margin: 0,
                    fontSize: 15,
                    color: '#4B5563',
                    lineHeight: 1.65,
                  }}
                >
                  {p.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

/* ============================================================
   Outils
   ============================================================ */
type ToolTone = 'yellow' | 'teal' | 'indigo' | 'neutral';
const Outils = () => {
  const tools: {
    name: string;
    vendor: string;
    desc: string;
    tag: string;
    tone: ToolTone;
    logo: React.ReactNode;
  }[] = [
    {
      name: 'Gemini',
      vendor: 'Google',
      desc: "L'assistant conversationnel de Google. Permet aux élèves de poser des questions, de se faire expliquer un mot difficile, ou de générer des idées créatives — toujours sous le regard de l'enseignant.",
      tag: 'Assistant',
      tone: 'yellow',
      logo: (
        <svg viewBox="0 0 24 24" width="32" height="32">
          <path fill={YELLOW} d="M12 2 13.5 8.5 20 10l-6.5 1.5L12 18l-1.5-6.5L4 10l6.5-1.5L12 2z" />
        </svg>
      ),
    },
    {
      name: 'Canva Éducation',
      vendor: 'Canva',
      desc: 'La plateforme créative utilisée par des millions d\'enseignants dans le monde. Nos élèves l\'utilisent pour donner vie à leurs idées : affiches, mini-livres, présentations.',
      tag: 'Création',
      tone: 'teal',
      logo: (
        <svg viewBox="0 0 24 24" width="32" height="32">
          <circle cx="12" cy="12" r="10" fill={TEAL} />
          <path d="M16 9.5a4 4 0 1 0-1 5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      name: 'Google for Education',
      vendor: 'Google',
      desc: "Le cadre numérique sécurisé de l'école. Comptes dédiés, données protégées, environnement filtré et adapté aux enfants.",
      tag: 'Cadre sécurisé',
      tone: 'indigo',
      logo: (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
          <path d="M12 4v8H4a8 8 0 1 1 8-8z" fill={INDIGO} />
          <circle cx="12" cy="12" r="3" fill="#fff" />
        </svg>
      ),
    },
    {
      name: 'Makeblock',
      vendor: 'Robotique éducative',
      desc: "Pour faire le pont entre l'IA et le monde physique. Les enfants découvrent que coder un comportement, c'est déjà donner une forme d'intelligence à un objet.",
      tag: 'Robotique',
      tone: 'neutral',
      logo: (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#374151" strokeWidth="1.75">
          <rect x="5" y="8" width="14" height="11" rx="2" />
          <path d="M9 8V5a3 3 0 0 1 6 0v3M9 13h.01M15 13h.01M9 16h6" strokeLinecap="round" />
        </svg>
      ),
    },
  ];
  const tones: Record<ToolTone, { bg: string; fg: string }> = {
    yellow: { bg: 'rgba(251,185,0,0.10)', fg: YELLOW_DARK },
    teal: { bg: 'rgba(0,145,158,0.10)', fg: TEAL },
    indigo: { bg: `${INDIGO}1A`, fg: INDIGO },
    neutral: { bg: '#F3F4F6', fg: '#374151' },
  };
  return (
    <section style={{ padding: '96px 0', background: '#fff' }}>
      <Container>
        <SectionHeading
          eyebrow="Les outils"
          title="Des technologies reconnues,"
          emphasis="dans un cadre éducatif"
          intro="Nous avons choisi des outils éprouvés, conçus ou adaptés pour l'éducation, utilisés dans les meilleures écoles à travers le monde."
        />
        <div className="grid gap-5 md:grid-cols-2">
          {tools.map((t, i) => {
            const tone = tones[t.tone];
            return (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px -20px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.boxShadow = '';
                }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: 22,
                  background: '#fff',
                  borderRadius: 14,
                  padding: 28,
                  border: '1px solid #E5E7EB',
                  transition: 'transform .25s, box-shadow .25s',
                }}
              >
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 14,
                    background: tone.bg,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {t.logo}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>{t.name}</h3>
                    <span style={{ fontSize: 13, color: '#9CA3AF' }}>{t.vendor}</span>
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: tone.fg,
                        background: tone.bg,
                        padding: '4px 10px',
                        borderRadius: 999,
                      }}
                    >
                      {t.tag}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 14.5, color: '#4B5563', lineHeight: 1.65 }}>{t.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

/* ============================================================
   Sécurité (dark)
   ============================================================ */
const Securite = () => {
  const rules = [
    {
      title: 'Aucune information personnelle',
      body: "Aucune donnée privée n'est jamais partagée avec une IA. Nom, prénom, adresse, photo : ces informations restent strictement hors écran. Les enfants apprennent à protéger leur vie privée comme un réflexe.",
      icon: (
        <path
          d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: "L'adulte d'abord",
      body: 'En cas de doute, de réponse étrange, ou de message qui met mal à l\'aise : on s\'arrête, on appelle l\'enseignant. Ce réflexe est aussi important que celui de traverser sur un passage piéton.',
      icon: (
        <path
          d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.008H9.375V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.008H15V9.75z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: "L'esprit critique avant tout",
      body: "L'IA peut se tromper, et elle se trompe. Nos élèves apprennent à vérifier, à douter sainement, à confronter ce que dit la machine à ce qu'ils savent ou peuvent vérifier ailleurs. C'est cette compétence-là, plus que toute autre, qui les protègera toute leur vie.",
      icon: (
        <path
          d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM10.5 7.5v6m3-3h-6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
  ];
  return (
    <section
      style={{
        padding: '100px 0',
        background: 'linear-gradient(180deg, #1F2937 0%, #111827 100%)',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: -120,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 600,
          height: 600,
          background: `${INDIGO}33`,
          borderRadius: '50%',
          filter: 'blur(120px)',
        }}
      />
      <Container>
        <SectionHeading
          eyebrow="Nos 3 engagements"
          title="La sécurité de votre enfant,"
          emphasis="notre première règle"
          intro="Avant tout apprentissage, nos élèves intègrent trois règles d'or, simples, claires, et rappelées à chaque séance. Elles forment le socle de notre programme."
          light
        />

        <div className="relative grid gap-5 md:grid-cols-3">
          {rules.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 18,
                padding: 32,
                backdropFilter: 'blur(8px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 14,
                    background: 'rgba(251,185,0,0.15)',
                    color: YELLOW,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                    {r.icon}
                  </svg>
                </div>
                <span
                  style={{
                    fontSize: 56,
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.10)',
                    fontFamily: "'Gruppo', sans-serif",
                    lineHeight: 1,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1.25 }}>{r.title}</h3>
              <p style={{ margin: 0, fontSize: 15, color: '#D1D5DB', lineHeight: 1.65 }}>{r.body}</p>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
};

/* ============================================================
   Bénéfices
   ============================================================ */
const Benefices = () => {
  const items = [
    {
      title: 'Esprit critique',
      body: "Apprendre à questionner ce qu'on lit, ce qu'on entend, ce qu'une machine répond. Une compétence transversale qui sert dans toutes les matières et toute la vie.",
      icon: (
        <path
          d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0zm-9 5.25h.008v.008H12v-.008z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: 'Créativité augmentée',
      body: "L'IA peut être un partenaire de création — pour écrire une histoire, illustrer une idée, donner forme à un projet. Nos élèves apprennent à rester l'auteur, en utilisant l'outil sans s'effacer derrière lui.",
      icon: (
        <path
          d="M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: 'Citoyenneté numérique',
      body: 'Comprendre comment fonctionne le monde dans lequel ils grandissent. Savoir où sont les limites, les risques, les opportunités. Devenir des citoyens lucides du numérique.',
      icon: (
        <path
          d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      title: 'Confiance et autonomie',
      body: "Ne pas avoir peur de la technologie, mais ne pas non plus s'y soumettre. Trouver sa juste place — celle de l'humain qui dirige, et non qui suit.",
      icon: (
        <path
          d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
  ];
  return (
    <section style={{ padding: '100px 0', background: '#F9FAFB' }}>
      <Container>
        <SectionHeading
          eyebrow="Pour votre enfant"
          title="Plus que la technologie :"
          emphasis="des compétences pour la vie"
        />
        <div className="grid gap-6 md:grid-cols-2">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 24,
                background: '#fff',
                borderRadius: 16,
                padding: 32,
                boxShadow: '0 10px 30px -10px rgba(0,0,0,0.08)',
                border: '1px solid #F3F4F6',
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 14,
                  background: i % 2 === 0 ? `${INDIGO}1A` : 'rgba(251,185,0,0.12)',
                  color: i % 2 === 0 ? INDIGO : YELLOW_DARK,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                  {it.icon}
                </svg>
              </div>
              <div>
                <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                  {it.title}
                </h3>
                <p style={{ margin: 0, fontSize: 15, color: '#4B5563', lineHeight: 1.65 }}>{it.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
};

/* ============================================================
   FAQ
   ============================================================ */
const Faq = () => {
  const qa = [
    {
      q: 'Mon enfant utilisera-t-il un ordinateur seul ?',
      a: "Non. Toute interaction avec une IA se fait dans le cadre de la classe, en présence de l'enseignant, avec une activité préparée. Aucune utilisation libre n'est laissée à l'élève.",
    },
    {
      q: 'Quelles données sont collectées sur mon enfant ?',
      a: "Aucune donnée personnelle n'est saisie dans les outils d'IA. L'école utilise des comptes scolaires Google for Education, qui offrent un cadre dédié à l'éducation, sans publicité et avec des protections de données renforcées.",
    },
    {
      q: 'Mon enfant va-t-il passer plus de temps devant un écran ?',
      a: "Non. Les temps d'usage du numérique restent mesurés et alternent toujours avec des temps de réflexion, d'écriture et d'échange en classe. L'IA est un outil parmi d'autres, pas une finalité.",
    },
    {
      q: "Faut-il que mon enfant soit déjà à l'aise avec l'informatique ?",
      a: "Pas du tout. Le programme est conçu pour des enfants qui découvrent. L'enseignant guide chaque étape.",
    },
    {
      q: "Comment l'IA s'intègre-t-elle aux apprentissages classiques ?",
      a: "Elle vient les enrichir, sans les remplacer. L'IA peut aider à explorer un mot de vocabulaire, illustrer une idée, ou créer une affiche — mais c'est toujours en lien avec une compétence travaillée en classe (lecture, expression, créativité, raisonnement).",
    },
  ];
  const [open, setOpen] = useState<number>(0);
  return (
    <section style={{ padding: '100px 0', background: '#fff' }}>
      <Container>
        <SectionHeading eyebrow="FAQ" title="Vos questions," emphasis="nos réponses" />
        <div style={{ maxWidth: 820, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {qa.map((it, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                style={{
                  background: isOpen ? '#FAFBFC' : '#fff',
                  border: `1px solid ${isOpen ? '#E5E7EB' : '#F3F4F6'}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  transition: 'background .2s, border-color .2s',
                }}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '20px 24px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    fontFamily: 'inherit',
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: INDIGO,
                      fontFamily: 'monospace',
                      flexShrink: 0,
                      width: 28,
                    }}
                  >
                    Q{String(i + 1).padStart(2, '0')}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 16.5,
                      fontWeight: 600,
                      color: '#111827',
                      lineHeight: 1.45,
                    }}
                  >
                    {it.q}
                  </span>
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: isOpen ? INDIGO : '#F3F4F6',
                      color: isOpen ? '#fff' : '#374151',
                      display: 'grid',
                      placeItems: 'center',
                      transition: 'all .2s',
                      transform: isOpen ? 'rotate(45deg)' : 'none',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>
                {isOpen && (
                  <div
                    style={{
                      padding: '0 24px 22px 68px',
                      fontSize: 15,
                      color: '#4B5563',
                      lineHeight: 1.7,
                    }}
                  >
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

/* ============================================================
   CTA Final
   ============================================================ */
const CtaFinal = () => (
  <section style={{ padding: '100px 0', background: '#F9FAFB', position: 'relative', overflow: 'hidden' }}>
    <Container>
      <div
        style={{
          position: 'relative',
          background: `linear-gradient(135deg, ${INDIGO} 0%, ${INDIGO}E6 60%, #5B21B6 100%)`,
          borderRadius: 28,
          padding: '72px 64px',
          color: '#fff',
          overflow: 'hidden',
          boxShadow: `0 30px 60px -25px ${INDIGO}80`,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: -80,
            right: -80,
            width: 280,
            height: 280,
            background: 'rgba(251,185,0,0.20)',
            borderRadius: '50%',
            filter: 'blur(40px)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 200,
            height: 200,
            background: 'rgba(255,255,255,0.10)',
            borderRadius: '50%',
            filter: 'blur(30px)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 32,
            right: 56,
            width: 28,
            height: 28,
            background: YELLOW,
            borderRadius: 8,
            transform: 'rotate(15deg)',
            opacity: 0.85,
          }}
        />

        <div className="relative grid gap-10 md:grid-cols-[1.4fr_1fr] md:gap-14 items-center">
          <div>
            <p
              style={{
                fontSize: 13,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
                color: YELLOW,
                margin: '0 0 14px',
              }}
            >
              Inscription 2026 / 2027
            </p>
            <h2
              style={{
                margin: '0 0 20px',
                fontSize: 40,
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
                color: '#fff',
                textWrap: 'balance' as React.CSSProperties['textWrap'],
              }}
            >
              Donnez à votre enfant une longueur d'avance, en confiance
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: 17,
                lineHeight: 1.65,
                color: 'rgba(255,255,255,0.85)',
                maxWidth: 560,
              }}
            >
              Le Programme IA est intégré à notre cycle primaire. Chaque enfant qui rejoint le Groupe Scolaire Sanaa bénéficie de cette préparation au monde de demain — dans un cadre humain, encadré, et tourné vers l'épanouissement.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Link
              to="/inscription"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                background: YELLOW,
                color: '#111827',
                padding: '18px 24px',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 700,
                textDecoration: 'none',
                boxShadow: '0 12px 28px -10px rgba(251,185,0,0.5)',
              }}
            >
              Inscrire mon enfant
              <Arrow />
            </Link>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ============================================================
   Page
   ============================================================ */
const ProgrammeIAPage = () => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <Hero />
      <Pourquoi />
      <Approche />
      <Outils />
      <Securite />
      <Benefices />
      <Faq />
      <CtaFinal />
    </motion.div>
  );
};

export default ProgrammeIAPage;
